import { Injectable, inject, signal, effect } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { LanguageService } from '@core/services/language.service';

@Injectable({ providedIn: 'root' })
export class ValidationService {
  private languageService = inject(LanguageService);

  currentLanguage = signal<string>('es');

  constructor() {
    effect(() => {
      this.currentLanguage.set(this.languageService.currentLanguage());
    });
  }

  private errorMessages: {
    [key: string]: (field: string, error?: any) => string;
  } = {
    required: (field) =>
      `${field} ${this.currentLanguage() === 'es' ? 'es requerido' : 'is required'}`,
    minlength: (field, error: { requiredLength: number }) =>
      `${field} debe tener al menos ${error.requiredLength} caracteres`,
    maxlength: (field, error: { requiredLength: number }) =>
      `${field} debe tener máximo ${error.requiredLength} caracteres`,
    min: (field, error: { min: number }) =>
      `${field} debe ser mayor o igual a ${error.min}`,
    max: (field, error: { max: number }) =>
      `${field} debe ser menor o igual a ${error.max}`,
    email: (field) => `Ingresa un ${field} válido`,
    pattern: (field) => `${field} no cumple con el formato requerido`,
    requiredTrue: (field) => `${field} debe estar seleccionado`,
    whitespace: (field) => `${field} no puede contener solo espacios en blanco`,
    integer: (field) => `${field} debe ser un número entero`,
    positive: (field) => `${field} debe ser un número positivo`,
    onlyNumbers: (field) => `${field} debe contener solo números`,
    onlyText: (field) => `${field} debe contener solo letras y espacios`,
  };

  /**
   * Retorna los mensajes de error del control listos para mostrar en el template.
   * Por defecto solo muestra errores cuando el control está `touched` O `dirty`.
   * Pasar `showOnDirty = false` para mostrar solo tras `touched` (útil en submit).
   * Usar `errors[0]` en el template — el array puede tener más de un error.
   */
  getAllErrors(
    control: AbstractControl | null | undefined,
    fieldName: string,
    showOnDirty: boolean = true,
  ): string[] {
    if (!control || !control.errors) return [];
    if (!control.touched && (!showOnDirty || !control.dirty)) return [];

    return Object.keys(control.errors).map((errorKey) => {
      if (this.errorMessages[errorKey]) {
        return this.errorMessages[errorKey](
          fieldName,
          control.errors?.[errorKey],
        );
      }
      return `Error en ${fieldName}: ${errorKey}`;
    });
  }
}
